import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Row,
  Switch,
  DatePicker,
  message as antMessage,
  Upload,
  Tooltip,
  Divider
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  CheckOutlined,
  CloseOutlined,
  UploadOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import moment from 'moment';
import useAuthContext from '../../contexts/AuthContext';
import useErrorMessage from '../../utils/ErrorMessage';
import useContractsContext from './ContractsContext';

const { Option } = Select;

const UpdateContractStatusInfo = ({
  contract,
  id,
  getContract,
  switchTerminationRootContract
}) => {
  const { t } = useTranslation();
  const { updateStatusVisible, setUpdateStatusVisible } = useContractsContext();
  const [form] = Form.useForm();
  const { dispatchAPI, user } = useAuthContext();
  const { message } = useErrorMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [upload, setUpload] = useState(null);
  const [confirmation, setConfirmation] = useState(true);
  const dateFormat = 'DD/MM/YYYY';
  const isCustomer = user?.role === 'guests:GUEST';

  const oneMonthAgo = moment().subtract(1, 'month');
  const endOfYear = moment().endOf('year');
  const effectiveDate = moment(contract?.effective_date);
  const currentDate = moment();

  const terminationReasons = [
    ...(contract?.status !== 'PENDING' && !contract?.previous_contract
      ? ['TERMINATED_CLASSIC']
      : []),
    ...(contract?.status !== 'PENDING' && !contract?.previous_contract
      ? ['TERMINATED_AMICABLE']
      : []),
    ...((moment(effectiveDate).month() === moment().month() &&
      !isCustomer &&
      contract?.status === 'ACTIVE' &&
      moment(contract?.quittances[0]?.period_date).isAfter(moment())) ||
    (contract?.status === 'PENDING' && moment(effectiveDate).isAfter(moment()))
      ? ['TERMINATION_WITHOUT_EFFECT']
      : [])
  ];

  const updateContractStatusInfo = async (body) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('values', JSON.stringify(body));
      formData.append('file', upload);
      const response = await dispatchAPI('PATCH', {
        url: `/contracts/statusinfo/${
          contract?.previous_contract ? contract._id : id
        }${contract?.previous_contract ? `?number=${contract.number}` : ''}
          ${isCustomer ? '&action=askTermination' : ''}`,
        body: formData
      });
      if (response.status === 200) {
        antMessage.success(
          t(
            `contracts.form.contract_status_info.success${
              isCustomer ? '_customer' : ''
            }`
          )
        );
      }
      setIsLoading(false);
      setUpload();
      setConfirmation(true);
      getContract();
      form.resetFields();
      setUpdateStatusVisible(false);
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const validateContractStatusInfo = async (values) => {
    await updateContractStatusInfo({
      ...values
    });
  };

  const onClose = () => {
    setConfirmation(true);
    setUpload();
    setForceRefresh(!forceRefresh);
    setUpdateStatusVisible(false);
  };

  const handleChange = () => {
    form.setFieldsValue({ termination_date: null });
  };

  useEffect(() => {
    if (updateStatusVisible) {
      form.resetFields();
    }
    // eslint-disable-next-line
  }, [updateStatusVisible]);

  const formLayout = {
    wrapperCol: { span: 12 }
  };

  return (
    <div
      id="update-contract-status-btn"
      style={{ float: 'right', position: 'relative', top: -35 }}
    >
      <Drawer
        title={
          <div style={{ textAlign: 'center' }}>
            {t(
              `contracts.form.contract_status_info.drawer${
                isCustomer ? '_customer' : ''
              }${contract?.previous_contract ? '_amendment' : ''}`
            )}
          </div>
        }
        placement="right"
        open={updateStatusVisible}
        onClose={onClose}
        closable
        size={430}
      >
        <Form
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...formLayout}
          form={form}
          layout="vertical"
          onFinish={validateContractStatusInfo}
          initialValues={{
            ...contract,
            termination_reason: undefined
          }}
        >
          {contract?.status === 'WITHOUT_EFFECT' ? (
            <div
              style={{
                marginBottom: 14,
                textAlign: 'center',
                fontWeigh: 'bold',
                color: 'red'
              }}
            >
              {`Cet avenant est dèjà sans-effet depuis le ${moment(
                contract.termination_date
              ).format('DD/MM/YY')}.`}
            </div>
          ) : (
            <div
              style={{
                marginBottom: 14,
                textAlign: 'center',
                fontWeigh: 'bold'
              }}
            >
              {`Vous êtes sur le point de ${t(
                `contracts.form.contract_status_info.termination${
                  contract?.previous_contract ? '_amendment' : ''
                }${isCustomer ? '_customer' : ''}`
              )} ${
                contract?.previous_contract ? "l'avenant" : 'le contrat'
              } N° ${contract?.number} ${
                contract?.amendment?.length ? 'ainsi que ses avenants' : ''
              }.`}
            </div>
          )}
          {!terminationReasons.length && contract && (
            <div>
              <p
                style={{
                  textAlign: 'center',
                  fontWeight: 'bold',
                  marginTop: 15,
                  marginBottom: 40
                }}
              >
                {t(
                  'contracts.form.contract_status_info.without_effect_not_possible'
                )}
              </p>
              <Divider />
              <p style={{ marginTop: 40 }}>
                {t('contracts.form.contract_status_info.termination_switch')}
              </p>
              <div style={{ textAlign: 'center' }}>
                <Button type="primary" onClick={switchTerminationRootContract}>
                  {t('buttons.validate')}
                  <CheckOutlined />
                </Button>
              </div>
            </div>
          )}
          {terminationReasons.length ? (
            <>
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => (
                  <>
                    <Form.Item
                      name={['termination_reason']}
                      label={t(
                        'contracts.form.contract_status_info.termination_reason'
                      )}
                      rules={[
                        { required: true, message: t('errors.form.REQUIRED') }
                      ]}
                    >
                      <Select style={{ width: 280 }} onChange={handleChange}>
                        {terminationReasons.map((s) => (
                          <Option key={s} value={s}>
                            <Tooltip
                              placement="left"
                              title={t(`contracts.tooltip.${s}`)}
                            >
                              {t(`contracts.tags.${s}`)}
                            </Tooltip>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="termination_justification"
                      label={t('absences.justificative')}
                      style={{
                        marginTop: 9
                      }}
                      rules={[
                        {
                          required:
                            getFieldValue('termination_reason') !==
                            'TERMINATION_WITHOUT_EFFECT',
                          message: t('errors.form.REQUIRED')
                        }
                      ]}
                    >
                      <Upload
                        maxCount={1}
                        beforeUpload={(file) => {
                          setUpload(file);
                          return false;
                        }}
                        onRemove={() => setUpload()}
                      >
                        <Button icon={<UploadOutlined />}>
                          {t('buttons.upload')}
                        </Button>
                      </Upload>
                    </Form.Item>
                    {getFieldValue('termination_reason') ? (
                      <Form.Item
                        name={['termination_date']}
                        tooltip={
                          getFieldValue('termination_reason') ===
                          'TERMINATED_AMICABLE'
                            ? t('contracts.form.tooltip.amicable_termination')
                            : ''
                        }
                        label={t(
                          'contracts.form.contract_status_info.termination_date'
                        )}
                        rules={[
                          {
                            required:
                              getFieldValue('termination_reason') ===
                              'TERMINATED_AMICABLE',
                            message: t('errors.form.REQUIRED')
                          }
                        ]}
                      >
                        {getFieldValue('termination_reason') ===
                        'TERMINATED_AMICABLE' ? (
                          <DatePicker
                            format={dateFormat}
                            style={{ width: 200 }}
                            disabledDate={(current) => {
                              let before = current <= oneMonthAgo;
                              if (
                                moment(effectiveDate).isAfter(
                                  moment(oneMonthAgo)
                                )
                              )
                                before =
                                  current <=
                                  moment(effectiveDate).startOf('day');
                              const after = current >= endOfYear;
                              return before || after;
                            }}
                          />
                        ) : (
                          moment(
                            effectiveDate > currentDate
                              ? effectiveDate
                              : currentDate
                          )
                            .endOf(
                              `${
                                getFieldValue('termination_reason') ===
                                'TERMINATED_CLASSIC'
                                  ? 'year'
                                  : 'day'
                              }`
                            )
                            .format(
                              `DD/MM/YYYY ${
                                getFieldValue('termination_reason') ===
                                'TERMINATED_CLASSIC'
                                  ? 'à 23:59'
                                  : ''
                              }`
                            )
                        )}
                      </Form.Item>
                    ) : null}
                    <Form.Item
                      name={['termination_comment']}
                      label={t(
                        'contracts.form.contract_status_info.termination_comment'
                      )}
                    >
                      <Input.TextArea maxLength={20} autoSize />
                    </Form.Item>
                  </>
                )}
              </Form.Item>
              <Form.Item
                name={['email_sending']}
                label={t('contracts.form.contract_status_info.email_sending')}
                valuePropName="checked"
                tooltip={t('contracts.form.tooltip.email_sending')}
              >
                <Switch
                  checkedChildren={t('quotes.form.switch.yes')}
                  unCheckedChildren={t('quotes.form.switch.no')}
                />
              </Form.Item>
              <Form.Item
                name={['termination_confirmation']}
                label={t(
                  `contracts.form.contract_status_info.termination_confirmation${
                    contract?.previous_contract ? '_amendment' : ''
                  }${isCustomer ? '_customer' : ''}`
                )}
                valuePropName="checked"
              >
                <Switch
                  onChange={() => setConfirmation(!confirmation)}
                  checkedChildren={t('quotes.form.switch.yes')}
                  unCheckedChildren={t('quotes.form.switch.no')}
                />
              </Form.Item>

              <Row style={{ marginTop: 25 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  disabled={confirmation}
                  loading={isLoading}
                >
                  {t('buttons.validate')}
                  <CheckOutlined />
                </Button>
                <Button danger type="link" onClick={onClose}>
                  {t('buttons.cancel')}
                  <CloseOutlined />
                </Button>
              </Row>
            </>
          ) : (
            <></>
          )}
        </Form>
      </Drawer>
    </div>
  );
};

UpdateContractStatusInfo.propTypes = {
  contract: PropTypes.shape({
    amendment: PropTypes.arrayOf(PropTypes.shape({})),
    number: PropTypes.string,
    termination_reason: PropTypes.string,
    _id: PropTypes.string,
    previous_contract: PropTypes.string,
    termination_date: PropTypes.string,
    effective_date: PropTypes.string,
    status: PropTypes.string,
    quittances: PropTypes.arrayOf(
      PropTypes.shape({ period_date: PropTypes.string })
    )
  }).isRequired,
  id: PropTypes.string.isRequired,
  getContract: PropTypes.func.isRequired,
  switchTerminationRootContract: PropTypes.func.isRequired
};

export default UpdateContractStatusInfo;
